import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isStaff } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Lock, Mail, Smartphone, Leaf, Eye, EyeOff, Phone } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate(isStaff(user.role) ? "/admin" : "/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const done = (data) => {
    setUser(data);
    toast.success(`Welcome, ${data.name || data.email}!`);
    navigate(isStaff(data.role) ? "/admin" : "/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f8faf8]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-emerald-900 text-white p-12 grain relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <img src="/logo.png" alt="NovaAgri" className="h-10 w-10 rounded-lg bg-white/10 object-contain p-0.5" />
          <div>
            <div className="font-head font-extrabold">NovaAgri Solution LLP</div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-300/70">Agriculture Technology</div>
          </div>
        </Link>
        <div className="relative z-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold"><Leaf className="h-4 w-4" /> Empowering Farmers Across India</span>
          <h2 className="mt-5 font-head font-extrabold text-4xl leading-tight">Apply for farm subsidies without the paperwork stress.</h2>
          <p className="mt-5 text-emerald-100/80 max-w-md">Sign in to start and track your tractor subsidy application securely.</p>
          <div className="mt-8 flex gap-6 text-sm">
            <div><div className="font-head font-extrabold text-2xl">28</div><div className="text-emerald-200/60">States</div></div>
            <div><div className="font-head font-extrabold text-2xl">Up to 50%</div><div className="text-emerald-200/60">Subsidy</div></div>
            <div><div className="font-head font-extrabold text-2xl">End-to-End</div><div className="text-emerald-200/60">Support</div></div>
          </div>
        </div>
        <div className="text-xs text-emerald-200/50 relative z-10">© 2026 NovaAgri Solution LLP</div>
      </div>

      {/* Right auth */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md rounded-3xl border-emerald-100 shadow-xl shadow-emerald-900/5 p-7 sm:p-9">
          <Link to="/" className="lg:hidden flex items-center gap-2 text-sm text-emerald-700 mb-6"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
          <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950">Sign in to continue</h1>
          <p className="mt-1.5 text-slate-500 text-sm">Choose a method to log in as a farmer or officer.</p>

          <Tabs defaultValue="password" className="mt-6">
            <TabsList className="grid grid-cols-2 w-full rounded-full bg-emerald-50 p-1 h-11">
              <TabsTrigger value="password" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-emerald-800" data-testid="tab-password"><Lock className="h-4 w-4 mr-1.5" /> Password</TabsTrigger>
              <TabsTrigger value="otp" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-emerald-800" data-testid="tab-phone-otp"><Smartphone className="h-4 w-4 mr-1.5" /> Phone OTP</TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="mt-6"><PasswordForm onDone={done} /></TabsContent>
            <TabsContent value="otp" className="mt-6"><PhoneOtpForm onDone={done} /></TabsContent>
          </Tabs>

          <div className="relative mt-6 mb-1 py-3"><div className="border-t border-slate-200" /><span className="absolute inset-x-0 -top-2.5 mx-auto w-fit bg-white px-3 text-xs text-slate-400">or</span></div>

          <GoogleButton />

          <p className="mt-6 text-center text-xs text-slate-400">By continuing you agree to our <Link to="/privacy-policy" className="text-emerald-700 hover:underline">Privacy Policy</Link>.</p>
        </Card>
      </div>
    </div>
  );
}

const GoogleButton = () => {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button onClick={signIn} variant="outline" className="w-full rounded-full bg-white hover:bg-slate-50 text-slate-700 border-slate-200 h-12" data-testid="google-login-button">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-3" /> Continue with Google
    </Button>
  );
};

const PasswordForm = ({ onDone }) => {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { email, password, name };
      const res = await api.post(url, payload);
      onDone(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {mode === "register" && (
        <div>
          <Label className="text-slate-600">Full Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ramesh Kumar" className="mt-1.5 rounded-xl h-11" data-testid="pw-name-input" />
        </div>
      )}
      <div>
        <Label className="text-slate-600">Email</Label>
        <div className="mt-1.5 relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="pl-9 rounded-xl h-11" data-testid="pw-email-input" />
        </div>
      </div>
      <div>
        <Label className="text-slate-600">Password</Label>
        <div className="mt-1.5 relative">
          <Input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pr-10 rounded-xl h-11" data-testid="pw-password-input" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand hover:bg-emerald-800 text-white h-11" data-testid="pw-submit-button">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{mode === "login" ? "Log In" : "Create Account"} <ArrowRight className="ml-2 h-4 w-4" /></>}
      </Button>
      <p className="text-center text-sm text-slate-500">
        {mode === "login" ? "New here? " : "Already have an account? "}
        <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-emerald-700 font-semibold hover:underline" data-testid="pw-toggle-mode">
          {mode === "login" ? "Create an account" : "Log in"}
        </button>
      </p>
    </form>
  );
};

const PhoneOtpForm = ({ onDone }) => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/phone-otp/send", { phone });
      toast.success(res.data.message);
      setSessionId(res.data.session_id);
      if (res.data.dev_otp) { setDevOtp(res.data.dev_otp); setOtp(res.data.dev_otp); }
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/phone-otp/verify", { phone, session_id: sessionId, otp, name: name || undefined });
      onDone(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  if (step === 1) {
    return (
      <form onSubmit={send} className="space-y-4">
        <div>
          <Label className="text-slate-600">Full Name <span className="text-slate-400">(new users)</span></Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ramesh Kumar" className="mt-1.5 rounded-xl h-11" data-testid="otp-name-input" />
        </div>
        <div>
          <Label className="text-slate-600">Mobile Number</Label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-input h-11 px-3 focus-within:ring-2 focus-within:ring-emerald-600/40">
            <span className="flex items-center gap-1 text-slate-500 text-sm border-r pr-2"><Phone className="h-4 w-4" /> +91</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" className="flex-1 bg-transparent outline-none text-sm" data-testid="otp-phone-input" />
          </div>
          <p className="mt-1.5 text-xs text-slate-400">We'll send a one-time code to this number by SMS.</p>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand hover:bg-emerald-800 text-white h-11" data-testid="otp-send-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send OTP <ArrowRight className="ml-2 h-4 w-4" /></>}
        </Button>
      </form>
    );
  }

  return (
    <div className="space-y-5">
      {devOtp && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="dev-otp-banner">
          <strong>SMS not configured yet</strong> — your code is <span className="font-mono font-bold tracking-widest">{devOtp}</span> (pre-filled).
        </div>
      )}
      <p className="text-sm text-slate-500">Enter the 6-digit code sent to <strong className="text-slate-700">+91 {phone}</strong>.</p>
      <div className="flex justify-center">
        <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-code-input">
          <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
        </InputOTP>
      </div>
      <Button onClick={verify} disabled={loading} className="w-full rounded-full bg-brand hover:bg-emerald-800 text-white h-11" data-testid="otp-verify-button">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify & Continue <ArrowRight className="ml-2 h-4 w-4" /></>}
      </Button>
      <button onClick={() => { setStep(1); setOtp(""); setDevOtp(""); setSessionId(""); }} className="w-full text-sm text-slate-500 hover:text-emerald-700" data-testid="otp-change-phone">Change number / resend</button>
    </div>
  );
};
